/**
 * Copyable Elements
 * Click-to-copy functionality for elements with .copyable class
 * Add .show-copy-icon to display a copy icon on hover
 *
 * NOTE: `.copyable` is overloaded. On FORM CONTROLS it is the much older
 * multi-add-row marker meaning "carry this field's value down into the next
 * row" (see copy_values_from_previous() in js/financial.js, js/deals.js,
 * js/platforms.js, js/press.js). Those fields must NEVER be wired for
 * click-to-copy: a click on an <input> would write its empty textContent to
 * the clipboard, and a click on a <select> would write every option label —
 * either way the user's clipboard is destroyed the moment they touch a field
 * on an add/edit page, so copy/paste into the form becomes impossible.
 * Form controls are skipped below; click-to-copy stays for display elements.
 */

(function() {
  'use strict';

  // Fields whose .copyable means "copy down to the next row", not "click to copy".
  var FORM_CONTROL_SEL = 'input, select, textarea, button, option, [contenteditable]';

  // Copy to Clipboard Functionality
  function initCopyableElements() {
    document.querySelectorAll('.copyable').forEach(element => {
      if (element.matches(FORM_CONTROL_SEL)) { return; }

      // Add click listener
      element.addEventListener('click', function() {
        const textToCopy = this.textContent || this.innerText;

        // Use the Clipboard API
        navigator.clipboard.writeText(textToCopy).then(() => {
          // Show success feedback
          showCopyFeedback(this);
        }).catch(err => {
          console.error('Failed to copy:', err);
          // Fallback for older browsers
          fallbackCopyTextToClipboard(textToCopy, this);
        });
      });

      // Add hover effect for copyable elements
      element.style.cursor = 'pointer';
    });
  }

  function showCopyFeedback(element) {
    const originalBg = element.style.background;
    const originalTransition = element.style.transition;

    // Flash effect
    element.style.transition = 'all 0.2s ease';
    element.style.background = 'rgba(34, 197, 94, 0.2)';

    // If element has copy icon, show checkmark
    const copyIcon = element.querySelector('.copy-icon');
    if (copyIcon) {
      const originalIcon = copyIcon.innerHTML;
      copyIcon.innerHTML = '<i class="fas fa-check"></i>';
      copyIcon.style.color = '#22c55e';

      setTimeout(() => {
        copyIcon.innerHTML = originalIcon;
        copyIcon.style.color = '';
      }, 1000);
    }

    // Show notification only for elements with the copy icon (explicit copy-to-clipboard UI)
    if (window.StudioDome && window.StudioDome.notify && element.classList.contains('show-copy-icon')) {
      StudioDome.notify('Copied to clipboard!', {
        type: 'success',
        duration: 2000,
        icon: 'fa-check'
      });
    }

    setTimeout(() => {
      element.style.background = originalBg;
      element.style.transition = originalTransition;
    }, 200);
  }

  function fallbackCopyTextToClipboard(text, element) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      showCopyFeedback(element);
    } catch (err) {
      console.error('Fallback: Could not copy text', err);
    }

    document.body.removeChild(textArea);
  }

  // Add copy icons to elements with show-copy-icon class
  function addCopyIcons() {
    document.querySelectorAll('.show-copy-icon').forEach(element => {
      // Skip if icon already added
      if (element.querySelector('.copy-icon')) return;
      // Never inject markup into a form control (see the note at the top).
      if (element.matches(FORM_CONTROL_SEL)) return;

      const copyIcon = document.createElement('span');
      copyIcon.className = 'copy-icon';
      copyIcon.innerHTML = '<i class="fas fa-copy"></i>';
      copyIcon.style.cssText = `
        position: absolute;
        top: 50%;
        right: 0.5rem;
        transform: translateY(-50%);
        opacity: 0.5;
        transition: opacity 0.2s ease;
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.875rem;
        pointer-events: none;
        z-index: 10;
      `;

      // Ensure the element can contain the absolutely positioned icon
      const currentPosition = window.getComputedStyle(element).position;
      if (currentPosition === 'static') {
        element.style.position = 'relative';
      }

      // Use inline-block for inline elements to properly accommodate the icon
      const currentDisplay = window.getComputedStyle(element).display;
      if (currentDisplay === 'inline' || element.tagName.toLowerCase() === 'code') {
        element.style.display = 'inline-block';
        // Add padding to prevent icon overlap (icon width ~14px + spacing)
        element.style.paddingRight = '1.75rem';
      }

      element.appendChild(copyIcon);

      // Brighten icon on hover
      element.addEventListener('mouseenter', () => {
        copyIcon.style.opacity = '1';
      });

      element.addEventListener('mouseleave', () => {
        copyIcon.style.opacity = '0.5';
      });
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initCopyableElements();
      addCopyIcons();
    });
  } else {
    initCopyableElements();
    addCopyIcons();
  }

  // Export for dynamic content
  window.StudioDome = window.StudioDome || {};
  window.StudioDome.initCopyable = function() {
    initCopyableElements();
    addCopyIcons();
  };
})();


/* ============================================================
 * Table Row Link (UI-kit global behaviour)
 * ------------------------------------------------------------
 * Makes a table row clickable as a WHOLE when it has exactly ONE
 * action (a single <a href> or <button>) and no editable fields —
 * the row-level analogue of the whole-item-clickable callout.
 * This is the DEFAULT for every kit table; opt a row or table out
 * with `data-no-rowlink`.
 *
 * Lives here (already in modules.bundle.js) rather than its own file
 * so the change ships through the existing bundle without a build
 * re-wire. Paired CSS (.row-linked) is in
 * scss/themes/shared/components/_tables.scss.
 * ============================================================ */
(function () {
  'use strict';

  var FIELD_SEL  = 'input:not([type="hidden"]), select, textarea';
  var ACTION_SEL = 'a[href], button';
  // Interactive plugins manage their own row clicks — never hijack them.
  var SKIP_TABLE = '.handsontable, .dataTable, .ht_master, [data-no-rowlink]';

  // The single action element for a qualifying row, or null.
  function singleAction(row) {
    if (row.hasAttribute('data-no-rowlink')) { return null; }
    if (row.getAttribute('onclick'))         { return null; } // already wired
    if (row.querySelector(FIELD_SEL))        { return null; } // needs granular input
    var actions = row.querySelectorAll(ACTION_SEL);
    return actions.length === 1 ? actions[0] : null;
  }

  function enhance(row) {
    var ctrl = singleAction(row);
    if (!ctrl) { return; }
    row.classList.add('row-linked');
    row._rowLinkCtrl = ctrl;
  }

  // One delegated listener handles every row, including ones added later.
  function onDocClick(e) {
    var row = e.target.closest && e.target.closest('tr.row-linked');
    if (!row) { return; }
    // A click that lands on the control (or any interactive element) behaves normally.
    if (e.target.closest('a, button, input, select, textarea, label, [onclick]')) { return; }
    // Don't fire while the user is selecting text in the row.
    if (window.getSelection && String(window.getSelection())) { return; }
    var ctrl = row._rowLinkCtrl;
    if (!ctrl) { return; }
    if (ctrl.tagName === 'A' && ctrl.getAttribute('href')) {
      if (e.metaKey || e.ctrlKey || ctrl.target === '_blank') {
        window.open(ctrl.href, '_blank');
      } else {
        window.location.href = ctrl.href;
      }
    } else {
      ctrl.click();
    }
  }

  function initRowLinks(root) {
    root = root || document;
    var rows = root.querySelectorAll('table tbody tr, .table tbody tr');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.classList.contains('row-linked')) { return; }
      var tbl = row.closest('table, .table');
      if (tbl && tbl.matches && tbl.matches(SKIP_TABLE)) { return; }
      if (tbl && tbl.closest && tbl.closest('.handsontable-container')) { return; }
      enhance(row);
    });
  }

  document.addEventListener('click', onDocClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initRowLinks(); });
  } else {
    initRowLinks();
  }

  // Re-scan after injecting rows dynamically. (window.StudioDome is already
  // created by the copyable block above in this same bundle.)
  window.StudioDome.initRowLinks = initRowLinks;
})();